'use client';

import { useMemo } from 'react';

import { AnimatePresence, motion } from 'motion/react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { EMAIL_REGEX, NAME_REGEX } from '@/lib/validation';

import { ClearButton, FormFieldError, SuccessState } from './shared';
import { useFormState } from './use-form-state';

type FieldErrors = Partial<Record<'name' | 'email' | 'message', string>>;

interface ContactModalProps {
  open: boolean;
  onClose: () => void;
  source?: string;
}

const EMPTY_VALUES = { name: '', email: '', message: '' };
const MAX_MESSAGE_LENGTH = 500;

function validate(data: { name: string; email: string; message: string }): FieldErrors {
  const errors: FieldErrors = {};

  if (!data.email.trim()) {
    errors.email = 'Email is required';
  } else if (!EMAIL_REGEX.test(data.email.trim())) {
    errors.email = 'Please enter a valid email address';
  }

  if (data.name.trim() && !NAME_REGEX.test(data.name.trim())) {
    errors.name = 'Please enter a valid name (letters, spaces, hyphens, apostrophes)';
  }

  if (!data.message.trim()) {
    errors.message = 'Message is required';
  } else if (data.message.trim().length < 10) {
    errors.message = 'Please provide a bit more detail (at least 10 characters)';
  } else if (data.message.trim().length > MAX_MESSAGE_LENGTH) {
    errors.message = `Message must be ${MAX_MESSAGE_LENGTH} characters or less`;
  }

  return errors;
}

export function ContactModal({ open, onClose, source }: ContactModalProps) {
  const formConfig = useMemo(
    () => ({
      initialValues: EMPTY_VALUES,
      validate,
      endpoint: '/api/contact',
    }),
    [],
  );

  const { formState, errors, values, handleChange, clearField, handleSubmit } = useFormState(
    formConfig,
    open,
    source,
  );

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <AnimatePresence mode="wait">
          {formState === 'success' ? (
            <SuccessState
              title="Message sent!"
              subtitle="We'll get back to you as soon as possible."
              onClose={onClose}
            />
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex min-h-0 flex-1 flex-col"
            >
              <DialogHeader className="shrink-0">
                <DialogTitle className="text-2xl">Contact Us</DialogTitle>
                <DialogDescription>
                  Have a question or need help? We&apos;d love to hear from you.
                </DialogDescription>
              </DialogHeader>

              <form
                onSubmit={handleSubmit}
                noValidate
                className="mt-4 flex min-h-0 flex-1 flex-col"
              >
                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
                  <div>
                    <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium">
                      Name <span className="text-muted-foreground">(optional)</span>
                    </label>
                    <div className="relative">
                      <Input
                        id="contact-name"
                        name="name"
                        placeholder="Your name"
                        value={values.name}
                        aria-invalid={!!errors.name}
                        aria-describedby={errors.name ? 'contact-name-error' : undefined}
                        onChange={e => handleChange('name', e.target.value)}
                        className={values.name ? 'pr-8' : ''}
                      />
                      {values.name && <ClearButton onClick={() => clearField('name')} />}
                    </div>
                    <FormFieldError id="contact-name-error" error={errors.name} />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium">
                      Email <span className="text-destructive">*</span>
                    </label>
                    <div className="relative">
                      <Input
                        id="contact-email"
                        name="email"
                        type="email"
                        placeholder="you@company.com"
                        required
                        value={values.email}
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? 'contact-email-error' : undefined}
                        onChange={e => handleChange('email', e.target.value)}
                        className={values.email ? 'pr-8' : ''}
                      />
                      {values.email && <ClearButton onClick={() => clearField('email')} />}
                    </div>
                    <FormFieldError id="contact-email-error" error={errors.email} />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium">
                      Message <span className="text-destructive">*</span>
                    </label>
                    <div className="relative">
                      <Textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={4}
                        maxLength={MAX_MESSAGE_LENGTH}
                        placeholder="How can we help you?"
                        value={values.message}
                        aria-invalid={!!errors.message}
                        aria-describedby={errors.message ? 'contact-message-error' : undefined}
                        onChange={e => handleChange('message', e.target.value)}
                        className="resize-none"
                      />
                    </div>
                    <div className="mt-1 flex items-start justify-end gap-2">
                      <FormFieldError id="contact-message-error" error={errors.message} />
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {values.message.length}/{MAX_MESSAGE_LENGTH}
                      </span>
                    </div>
                  </div>

                  {formState === 'error' && (
                    <p className="text-sm text-destructive" role="alert">
                      Something went wrong. Please try again.
                    </p>
                  )}
                </div>

                <div className="shrink-0 pt-4">
                  <Button type="submit" className="w-full" disabled={formState === 'submitting'}>
                    {formState === 'submitting' ? 'Sending...' : 'Send Message'}
                  </Button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
